import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@vns/core';
import { supabase } from './supabase';
import { cleanDisplayRemarks, cleanWorkTypeName } from './transactions';

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

export interface DailyReportFilters {
  clientId?: string;
  projectId?: string;
  fromDate?: string;
  toDate?: string;
}

export interface DailyReportItem {
  id: string;
  date: string;
  type: 'payment' | 'material' | 'labour' | 'expense';
  typeLabel: string;
  mode: string;
  particulars: string;
  receiptPaise: number;
  payablePaise: number;
  runningBalancePaise: number;
  raw: Record<string, unknown>;
}

export interface DailyReportSummary {
  projectName: string;
  clientName: string;
  location: string;
  status: string;
  fromDate: string;
  toDate: string;
  totalReceiptsPaise: number;
  totalPayablesPaise: number;
  netRangeBalancePaise: number;
  openingBalancePaise: number;
  closingBalancePaise: number;
  materialTotalPaise: number;
  labourTotalPaise: number;
  expenseTotalPaise: number;
  transactionCount: number;
}

export interface DailyReportResult {
  items: DailyReportItem[];
  summary: DailyReportSummary;
}

export function useDailyReportData(filters: DailyReportFilters, enabled = true) {
  return useQuery({
    queryKey: ['daily-report', filters],
    enabled: enabled && !!filters.projectId,
    queryFn: async (): Promise<DailyReportResult> => {
      const { projectId, fromDate, toDate } = filters;
      if (!projectId) {
        throw new Error('Please select a project');
      }

      // 1. Fetch project header info
      const projectRow = unwrap(
        await supabase
          .from('projects')
          .select('id, name, location, status, client_id, clients(name)')
          .eq('id', projectId)
          .single(),
      );

      const projectName = projectRow.name;
      const clientName = (projectRow.clients as { name: string } | null)?.name ?? '—';
      const location = projectRow.location ?? '—';
      const status = projectRow.status;

      // 2. Fetch all approved transactions for this project
      const [paymentsRes, purchasesRes, labourRes, expensesRes, subPaymentsRes] = await Promise.all([
        supabase
          .from('client_payments')
          .select('id, paid_on, amount_paise, payment_type, reference_no, remarks')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('paid_on', { ascending: true }),

        supabase
          .from('material_purchases')
          .select('id, purchased_on, quantity, rate_paise, amount_paise, remarks, materials(name), units(short_name), suppliers(name)')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('purchased_on', { ascending: true }),

        supabase
          .from('subcontractor_labour')
          .select('id, worked_on, worker_count, rate_per_worker_paise, total_amount_paise, remarks, subcontractors(name), work_types(name)')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('worked_on', { ascending: true }),

        supabase
          .from('expenses')
          .select('id, spent_on, amount_paise, paid_to, remarks, expense_categories(name)')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('spent_on', { ascending: true }),

        supabase
          .from('subcontractor_payments')
          .select('id, paid_on, amount_paise, mode, reference_no, remarks, subcontractors(name)')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('paid_on', { ascending: true }),
      ]);

      const payments = unwrap(paymentsRes);
      const purchases = unwrap(purchasesRes);
      const labour = unwrap(labourRes);
      const expenses = unwrap(expensesRes);
      const subPayments = unwrap(subPaymentsRes);

      // 3. Unify all entries into a normalized list
      const rawAllItems: Array<{
        id: string;
        date: string;
        type: 'payment' | 'material' | 'labour' | 'expense';
        typeLabel: string;
        mode: string;
        particulars: string;
        receiptPaise: number;
        payablePaise: number;
        raw: Record<string, unknown>;
      }> = [];

      // A. Client Payments (Receipts)
      for (const p of payments) {
        // Mode of transaction (e.g. Cash, Cheque, Bank Transfer, NEFT, UPI)
        const mode = p.reference_no?.trim() || 'Cash';
        // Clean remarks: remove [DEBIT] tag if present
        const cleanRemarks = p.remarks?.replace(/\[DEBIT\]/gi, '').trim() || '';
        // Particulars without milestone or mode prefix
        const particulars = cleanRemarks ? `Client Payment & ${cleanRemarks}` : 'Client Payment';

        rawAllItems.push({
          id: `pay-${p.id}`,
          date: p.paid_on,
          type: 'payment',
          typeLabel: 'Receipt',
          mode,
          particulars,
          receiptPaise: Number(p.amount_paise || 0),
          payablePaise: 0,
          raw: p as unknown as Record<string, unknown>,
        });
      }

      // B. Material Purchases (Payables)
      for (const m of purchases) {
        const supplier = (m.suppliers as { name: string } | null)?.name || 'Supplier';
        const material = (m.materials as { name: string } | null)?.name || 'Material';
        const unit = (m.units as { short_name: string } | null)?.short_name || '';
        const qty = Number(m.quantity || 0);
        const qtyStr = qty > 0 ? `= ${qty.toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${unit}`.trim() : '';
        const cleanRemarks = m.remarks?.trim() || '';
        const remarksStr = cleanRemarks ? `& ${cleanRemarks}` : '';
        const particulars = `${supplier} / ${material} ${qtyStr} ${remarksStr}`.trim();
        const modeMatch = cleanRemarks.match(/\[(Cash|Cheque|Bank Transfer|NEFT|RTGS|UPI|Debit)\]/i);
        const mode: string = (modeMatch && modeMatch[1]) ? modeMatch[1] : 'Credit';

        rawAllItems.push({
          id: `mat-${m.id}`,
          date: m.purchased_on,
          type: 'material',
          typeLabel: 'Material',
          mode,
          particulars,
          receiptPaise: 0,
          payablePaise: Number(m.amount_paise || 0),
          raw: m as unknown as Record<string, unknown>,
        });
      }

      // C. Sub-contractor Labour (Work Done - Payables)
      for (const l of labour) {
        const sub = (l.subcontractors as { name: string } | null)?.name || 'Sub-contractor';
        const rawTrade = (l.work_types as { name: string } | null)?.name || 'Labour';
        const trade = cleanWorkTypeName(rawTrade);
        const workers = Number(l.worker_count || 0);
        const workerStr = workers > 0 ? `= ${workers} Nos` : '';
        const cleanRemarks = cleanDisplayRemarks(l.remarks) || '';
        const remarksStr = cleanRemarks ? `& ${cleanRemarks}` : '';
        const particulars = `${sub} / ${trade} ${workerStr} ${remarksStr}`.trim();
        const modeMatch = (l.remarks || '').match(/\[(Cash|Cheque|Bank Transfer|NEFT|RTGS|UPI|Debit)\]/i);
        const mode: string = (modeMatch && modeMatch[1]) ? modeMatch[1] : 'Credit';

        rawAllItems.push({
          id: `lab-${l.id}`,
          date: l.worked_on,
          type: 'labour',
          typeLabel: 'Labour',
          mode,
          particulars,
          receiptPaise: 0,
          payablePaise: Number(l.total_amount_paise || 0),
          raw: l as unknown as Record<string, unknown>,
        });
      }

      // C2. Sub-contractor Direct Payments (Disbursements / Debit)
      for (const sp of subPayments) {
        const sub = (sp.subcontractors as { name: string } | null)?.name || 'Sub-contractor';
        const cleanRemarks = cleanDisplayRemarks(sp.remarks) || '';
        const remarksStr = cleanRemarks ? `& ${cleanRemarks}` : '';
        const particulars = `Payment to ${sub} ${remarksStr}`.trim();
        const mode = sp.mode ? String(sp.mode).toUpperCase() : 'Cash';

        rawAllItems.push({
          id: `subpay-${sp.id}`,
          date: sp.paid_on,
          type: 'labour',
          typeLabel: 'Labour Paid',
          mode,
          particulars,
          receiptPaise: 0,
          payablePaise: Number(sp.amount_paise || 0),
          raw: sp as unknown as Record<string, unknown>,
        });
      }

      // D. Site Expenses (Payables)
      for (const e of expenses) {
        const paidTo = e.paid_to || 'Expense';
        const cat = (e.expense_categories as { name: string } | null)?.name || 'General Expense';
        const cleanRemarks = e.remarks?.replace(/\[(Cash|Cheque|Bank Transfer|NEFT|RTGS|UPI|Debit)\]/gi, '').trim() || '';
        const remarksStr = cleanRemarks ? `& ${cleanRemarks}` : '';
        const particulars = `${paidTo} / ${cat} ${remarksStr}`.trim();
        const modeMatch = e.remarks?.match(/\[(Cash|Cheque|Bank Transfer|NEFT|RTGS|UPI|Debit)\]/i);
        const mode: string = (modeMatch && modeMatch[1]) ? modeMatch[1] : 'Cash';

        rawAllItems.push({
          id: `exp-${e.id}`,
          date: e.spent_on,
          type: 'expense',
          typeLabel: 'Expense',
          mode,
          particulars,
          receiptPaise: 0,
          payablePaise: Number(e.amount_paise || 0),
          raw: e as unknown as Record<string, unknown>,
        });
      }


      // 4. Sort chronologically by date
      rawAllItems.sort((a, b) => a.date.localeCompare(b.date));

      // 5. Calculate Opening Balance (Prior to fromDate)
      let openingReceipts = 0;
      let openingPayables = 0;

      if (fromDate) {
        for (const item of rawAllItems) {
          if (item.date < fromDate) {
            openingReceipts += item.receiptPaise;
            openingPayables += item.payablePaise;
          }
        }
      }

      const openingBalancePaise = openingReceipts - openingPayables;

      // 6. Filter for active date range and compute running balance
      const filteredItems: DailyReportItem[] = [];
      let runningBalance = openingBalancePaise;

      let totalReceiptsPaise = 0;
      let totalPayablesPaise = 0;
      let materialTotalPaise = 0;
      let labourTotalPaise = 0;
      let expenseTotalPaise = 0;

      for (const item of rawAllItems) {
        const isAfterOrEqualFrom = !fromDate || item.date >= fromDate;
        const isBeforeOrEqualTo = !toDate || item.date <= toDate;

        if (isAfterOrEqualFrom && isBeforeOrEqualTo) {
          runningBalance = runningBalance + item.receiptPaise - item.payablePaise;

          totalReceiptsPaise += item.receiptPaise;
          totalPayablesPaise += item.payablePaise;

          if (item.type === 'material') materialTotalPaise += item.payablePaise;
          if (item.type === 'labour') labourTotalPaise += item.payablePaise;
          if (item.type === 'expense') expenseTotalPaise += item.payablePaise;

          filteredItems.push({
            ...item,
            runningBalancePaise: runningBalance,
          });
        }
      }

      const closingBalancePaise = runningBalance;
      const netRangeBalancePaise = totalReceiptsPaise - totalPayablesPaise;

      const summary: DailyReportSummary = {
        projectName,
        clientName,
        location,
        status,
        fromDate: fromDate || (filteredItems[0]?.date ?? '—'),
        toDate: toDate || (filteredItems[filteredItems.length - 1]?.date ?? '—'),
        totalReceiptsPaise,
        totalPayablesPaise,
        netRangeBalancePaise,
        openingBalancePaise,
        closingBalancePaise,
        materialTotalPaise,
        labourTotalPaise,
        expenseTotalPaise,
        transactionCount: filteredItems.length,
      };

      return {
        items: filteredItems,
        summary,
      };
    },
  });
}
